import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { formatAbsoluteDateTime } from './activity-ui';
import { sweepActivitySummary } from '@/lib/ui-editorial-sweeper';
import { useOptionalWorkspaceObjectives } from '@/lib/workspace-objectives';
import { readWorkSearchCursors, readWorkSearchPosition, rememberWorkSearchPosition, searchWork, workSearchPath, type WorkSearchCursor, type WorkSearchItem, type WorkSearchScope } from '@/lib/work-search';

type Entry = { id: string; event_at: string; subject_id: string | null; source_run_id: string | null; subject_label: string; actor_label: string; completion_meaning: string | null; outcome_summary: string | null; outcome_status: string | null; event_type: string; metadata: Record<string, unknown> };
type WorkDocumentRow = { id: string; source_id: string; current_version: number };
type WorkDocumentRevisionRow = { document_id: string; version: number; content: { title?: unknown } };
const PAGE_SIZE = 30;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SEARCH_SCOPES: { value: WorkSearchScope; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'active', label: 'En cours' },
  { value: 'waiting', label: 'En attente' },
  { value: 'blocked', label: 'Bloqué' },
  { value: 'history', label: 'Historique' },
  { value: 'results', label: 'Résultats' },
];
const SEARCH_CAPABILITIES = [
  { key: 'domino.web_research', label: 'Recherche internet' },
  { key: 'domino.draft_email', label: 'Préparer un email' },
] as const;
const eventLabel = (entry: Entry) => entry.completion_meaning === 'drafted' ? 'Brouillon préparé' : entry.completion_meaning === 'produced' ? 'Résultat produit' : entry.completion_meaning === 'sent' ? 'Message envoyé' : entry.event_type === 'action_skipped' ? 'Action écartée' : entry.outcome_status === 'failed' ? 'Travail interrompu' : 'Activité du compte';
export const ActivityJournalView = () => {
  const objectives = useOptionalWorkspaceObjectives()?.objectives ?? [];
  const location = useLocation();
  const [pageParams, setPageParams] = useSearchParams();
  const urlQuery = pageParams.get('q')?.trim() ?? '';
  const searchQuery = urlQuery.length >= 2 && urlQuery.length <= 120 ? urlQuery : '';
  const accountParam = pageParams.get('account');
  const searchAccountId = accountParam && UUID.test(accountParam) ? accountParam : null;
  const applicationParam = pageParams.get('application');
  const searchApplicationId = applicationParam && UUID.test(applicationParam) ? applicationParam : null;
  const searchCapabilityKey = SEARCH_CAPABILITIES.find(option => option.key === pageParams.get('action'))?.key ?? null;
  const objectiveParam = pageParams.get('objective');
  const searchObjectiveId = objectiveParam && UUID.test(objectiveParam) ? objectiveParam : null;
  const scopeParam = pageParams.get('scope');
  const searchScope = SEARCH_SCOPES.find(option => option.value === scopeParam)?.value ?? 'all';
  const pageCursors = readWorkSearchCursors(pageParams);
  const pageCursor = pageCursors.at(-1) ?? null;
  const [entries, setEntries] = useState<Entry[]>([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<{ at: string; id: string } | null>(null);
  const [documentTitles, setDocumentTitles] = useState<Record<string, string>>({});
  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const [searchReload, setSearchReload] = useState(0);
  const [searchItems, setSearchItems] = useState<WorkSearchItem[]>([]);
  const [searchNextCursor, setSearchNextCursor] = useState<WorkSearchCursor | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  useEffect(() => { setSearchDraft(searchQuery); }, [searchQuery]);
  useEffect(() => {
    if (!searchQuery) return;
    let alive = true;
    setSearchItems([]);
    setSearchNextCursor(null);
    setSearchLoading(true);
    setSearchError(null);
    void searchWork(searchQuery, pageCursor, searchAccountId, searchScope, searchApplicationId, searchCapabilityKey, searchObjectiveId).then((page) => {
      if (!alive) return;
      setSearchItems(page.items);
      setSearchNextCursor(page.nextCursor);
    }).catch(() => {
      if (alive) setSearchError('La recherche n’a pas pu être chargée.');
    }).finally(() => {
      if (alive) setSearchLoading(false);
    });
    return () => { alive = false; };
  }, [searchQuery, pageCursor?.at, pageCursor?.key, searchAccountId, searchApplicationId, searchCapabilityKey, searchObjectiveId, searchScope, searchReload]);
  useEffect(() => {
    if (searchLoading || searchItems.length === 0) return;
    const top = readWorkSearchPosition(location.key);
    if (top === null) return;
    const frame = window.requestAnimationFrame(() => document.querySelector('main')?.scrollTo({ top }));
    return () => window.cancelAnimationFrame(frame);
  }, [location.key, searchLoading, searchItems.length]);
  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    let query = supabase.from('activity_entries').select('id,event_at,subject_id,source_run_id,subject_label,actor_label,completion_meaning,outcome_summary,outcome_status,event_type,metadata')
      .not('subject_id','is',null).order('event_at',{ascending:false}).order('id',{ascending:false}).limit(PAGE_SIZE+1);
    if (cursor) query = query.or(`event_at.lt.${cursor.at},and(event_at.eq.${cursor.at},id.lt.${cursor.id})`);
    if (filter === 'documents') query = query.in('completion_meaning',['drafted','produced']);
    if (filter === 'attention') query = query.in('outcome_status',['failed','partial']);
    void Promise.resolve(query).then(({data,error}) => {
      if (!alive) return;
      if (error) { setError('L’activité des comptes n’a pas pu être chargée.'); return; }
      setHasMore((data?.length ?? 0)>PAGE_SIZE);
      const rows = (data ?? []).slice(0,PAGE_SIZE) as Entry[];
      setEntries(previous => cursor ? [...new Map([...previous,...rows].map(row => [row.id,row])).values()] : rows);
    }).catch(() => { if (alive) setError('L’activité des comptes n’a pas pu être chargée.'); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter,cursor]);
  const documentSourceSignature = entries
    .flatMap((entry) => {
      const taskId = typeof entry.metadata.taskActionRunId === 'string' ? entry.metadata.taskActionRunId : null;
      return [taskId, entry.source_run_id].filter((value): value is string => Boolean(value));
    })
    .sort()
    .join('|');
  useEffect(() => {
    const sourceIds = Array.from(new Set(documentSourceSignature.split('|').filter(Boolean)));
    if (sourceIds.length === 0) {
      setDocumentTitles({});
      return;
    }
    let alive = true;
    void (async () => {
      const documentsResponse = await supabase
        .from('work_documents')
        .select('id,source_id,current_version')
        .eq('result_tab_id', '').eq('result_section_id', '')
        .in('source_id', sourceIds);
      if (documentsResponse.error) return;
      const documents = (documentsResponse.data ?? []) as WorkDocumentRow[];
      if (documents.length === 0) return;
      const revisionsResponse = await supabase
        .from('work_document_revisions')
        .select('document_id,version,content')
        .in('document_id', documents.map((document) => document.id));
      if (revisionsResponse.error || !alive) return;
      const revisions = (revisionsResponse.data ?? []) as WorkDocumentRevisionRow[];
      const nextTitles: Record<string, string> = {};
      for (const document of documents) {
        const revision = revisions.find((item) => item.document_id === document.id && item.version === document.current_version);
        const title = revision?.content && typeof revision.content.title === 'string' ? revision.content.title.trim() : '';
        if (title) nextTitles[document.source_id] = title;
      }
      if (alive) setDocumentTitles(nextTitles);
    })();
    return () => { alive = false; };
  }, [documentSourceSignature]);
  return <section aria-label="Historique des comptes">
    <form className="mb-5 flex max-w-3xl flex-wrap gap-2" onSubmit={event => {
      event.preventDefault();
      const query = searchDraft.trim();
      if (query.length < 2) return;
      setSearchReload(current => current + 1);
      const params = new URLSearchParams(pageParams);
      params.set('q', query);
      params.delete('c');
      setPageParams(params);
    }}>
      <input type="search" aria-label="Rechercher dans les travaux et résultats"
        placeholder="Rechercher un travail ou un résultat…" minLength={2} maxLength={120}
        value={searchDraft} onChange={event => setSearchDraft(event.target.value)}
        className="min-w-48 flex-1 rounded-lg border border-domino-cardBorder bg-white px-3 py-2 text-sm" />
      <Button type="submit">Rechercher</Button>
      {searchQuery && <Button type="button" variant="secondary" onClick={() => {
        setSearchDraft(''); setSearchItems([]); setSearchNextCursor(null); setSearchError(null);
        const params = new URLSearchParams(pageParams);
        for (const key of ['q', 'account', 'application', 'action', 'objective', 'scope', 'c']) params.delete(key);
        setPageParams(params);
      }}>Effacer</Button>}
    </form>
    {searchQuery ? <div aria-label="Résultats de recherche">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <select aria-label="Filtrer les résultats" value={searchScope} onChange={event => {
          const params = new URLSearchParams(pageParams);
          if (event.target.value === 'all') params.delete('scope');
          else params.set('scope', event.target.value);
          params.delete('c');
          setPageParams(params);
        }} className="rounded-lg border border-domino-cardBorder bg-white px-3 py-2 text-sm">
          {SEARCH_SCOPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {objectives.length > 0 && <select aria-label="Filtrer par objectif" value={searchObjectiveId ?? ''} onChange={event => {
          const params = new URLSearchParams(pageParams);
          if (event.target.value) params.set('objective', event.target.value);
          else params.delete('objective');
          params.delete('c');
          setPageParams(params);
        }} className="max-w-full rounded-lg border border-domino-cardBorder bg-white px-3 py-2 text-sm">
          <option value="">Tous les objectifs</option>
          {objectives.map(objective => <option key={objective.id} value={objective.id}>{objective.title}</option>)}
        </select>}
        {searchObjectiveId && objectives.length === 0 && <button type="button" className="text-sm text-domino-aubergine underline underline-offset-4" onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('objective'); params.delete('c');
          setPageParams(params);
        }}>Tous les objectifs</button>}
        {searchAccountId && <button type="button" className="text-sm text-domino-aubergine underline underline-offset-4" onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('account'); params.delete('c');
          setPageParams(params);
        }}>Tous les comptes</button>}
        {searchApplicationId && <button type="button" className="text-sm text-domino-aubergine underline underline-offset-4" onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('application'); params.delete('c');
          setPageParams(params);
        }}>Toutes les applications</button>}
        {searchCapabilityKey && <button type="button" className="text-sm text-domino-aubergine underline underline-offset-4" onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('action'); params.delete('c');
          setPageParams(params);
        }}>Toutes les actions</button>}
      </div>
      {searchError && <p role="alert" className="py-4 text-sm text-red-700">{searchError}</p>}
      {searchItems.map(item => {
        const capability = SEARCH_CAPABILITIES.find(option => option.key === item.capability_key);
        const repeatsTitle = capability?.label === item.title.trim();
        const excerpt = item.excerpt.startsWith(item.title)
          ? item.excerpt.slice(item.title.length).trim()
          : item.excerpt;
        return <article key={item.item_key} className="border-t border-domino-cardBorder py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Link to={workSearchPath(item)} onClick={() => rememberWorkSearchPosition(location.key, document.querySelector('main')?.scrollTop ?? 0)} className="font-semibold text-domino-aubergine">
            {item.title}
          </Link>
          <time className="text-xs text-domino-inkSoft" dateTime={item.item_at}>{formatAbsoluteDateTime(item.item_at)}</time>
        </div>
        <p className="mt-1 text-xs text-domino-inkSoft">
          {searchAccountId || item.subject_kind !== 'company' ? item.subject_label : <button type="button" className="underline underline-offset-2 hover:text-domino-aubergine" aria-label={`Limiter la recherche au compte ${item.subject_label}`} onClick={() => {
            const params = new URLSearchParams(pageParams);
            params.set('account', item.subject_id); params.delete('c');
            setPageParams(params);
          }}>{item.subject_label}</button>}
          {item.version !== null ? ` · Version ${item.version}` : ''}
          {item.flow_id && item.application_label ? <> · {searchApplicationId ? item.application_label : <button type="button" className="underline underline-offset-2 hover:text-domino-aubergine" aria-label={`Limiter la recherche à l’application ${item.application_label}`} onClick={() => {
            const params = new URLSearchParams(pageParams);
            params.set('application', item.flow_id!); params.delete('action'); params.delete('c');
            setPageParams(params);
          }}>{item.application_label}</button>}</> : ''}
          {capability && (!searchCapabilityKey || !repeatsTitle) ? <> · {searchCapabilityKey ? capability.label : <button type="button" className="underline underline-offset-2 hover:text-domino-aubergine" aria-label={`Limiter la recherche à l’action ${capability.label}`} onClick={() => {
            const params = new URLSearchParams(pageParams);
            params.set('action', capability.key); params.delete('application'); params.delete('c');
            setPageParams(params);
          }}>{repeatsTitle ? 'Voir les autres résultats' : capability.label}</button>}</> : ''}
        </p>
        {excerpt && <p className="mt-1 line-clamp-2 text-sm leading-6 text-domino-inkSoft">{excerpt}</p>}
      </article>;})}
      {!searchLoading && !searchError && searchItems.length === 0 && <p className="py-8 text-sm text-domino-inkSoft">{searchObjectiveId ? 'Aucun travail lié à cet objectif pour cette recherche.' : 'Aucun travail ou résultat trouvé.'}</p>}
      {searchLoading && <p role="status" className="py-4 text-sm">Recherche en cours…</p>}
      <div className="flex flex-wrap gap-2">
        {pageCursors.length > 0 && <Button variant="secondary" disabled={searchLoading} onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('c');
          pageCursors.slice(0, -1).forEach(cursor => params.append('c', `${cursor.at}|${cursor.key}`));
          setPageParams(params);
        }}>Page précédente</Button>}
        {searchNextCursor && <Button variant="secondary" disabled={searchLoading} onClick={() => {
          const params = new URLSearchParams(pageParams);
          params.delete('c');
          pageCursors.forEach(cursor => params.append('c', `${cursor.at}|${cursor.key}`));
          params.append('c', `${searchNextCursor.at}|${searchNextCursor.key}`);
          setPageParams(params);
        }}>Page suivante</Button>}
      </div>
    </div> : <>
    <select aria-label="Filtrer l’historique" className="mb-5 rounded-lg border border-domino-cardBorder bg-white px-3 py-2 text-sm" value={filter} onChange={e => { setEntries([]); setFilter(e.target.value); setCursor(null); }}>
      <option value="all">Toute l’activité</option><option value="documents">Documents produits</option><option value="attention">Travaux interrompus</option>
    </select>
    {error && <p role="alert" className="py-4 text-sm text-red-700">{error}</p>}
    {entries.map(entry => {
      const taskId = typeof entry.metadata.taskActionRunId === 'string' ? entry.metadata.taskActionRunId : null;
      const author = /^[0-9a-f-]{36}$/i.test(entry.actor_label) || entry.actor_label === 'system' ? null : entry.actor_label;
      const summary = sweepActivitySummary(entry.outcome_summary, entry.outcome_status === 'failed');
      const documentTitle = (taskId ? documentTitles[taskId] : null) ?? (entry.source_run_id ? documentTitles[entry.source_run_id] : null) ?? null;
      return <article key={entry.id} className="border-t border-domino-cardBorder py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2"><Link to={`/threads/${entry.subject_id}${taskId ? `?result=${encodeURIComponent(taskId)}` : ''}`} className="font-semibold text-domino-aubergine">{entry.subject_label}</Link><time className="text-xs text-domino-inkSoft" dateTime={entry.event_at}>{formatAbsoluteDateTime(entry.event_at)}</time></div>
        <p className="mt-2 text-sm font-medium">{documentTitle ?? eventLabel(entry)}{author ? ` · ${author}` : ''}</p>
        {documentTitle && <p className="mt-0.5 text-xs text-domino-inkSoft">{eventLabel(entry)}</p>}
        {summary && <p className="mt-1 text-sm leading-6 text-domino-inkSoft">{summary}</p>}
      </article>;
    })}
    {!loading && !error && !entries.length && <p className="py-8 text-sm text-domino-inkSoft">Aucune activité de compte pour ce filtre.</p>}
    {loading && <p role="status" className="py-4 text-sm">Chargement de l’activité…</p>}
    {hasMore && <Button variant="secondary" disabled={loading} onClick={() => { const last = entries.at(-1); if (last) setCursor({at:last.event_at,id:last.id}); }}>Afficher les événements précédents</Button>}
    </>}
  </section>;
};
